import {
  faCalendarCheck,
  faCalendarPlus,
  faChartLine,
  faClipboardCheck,
  faClipboardList,
  faClockRotateLeft,
  faComments,
  faCreditCard,
  faFileInvoiceDollar,
  faFileLines,
  faFlagCheckered,
  faIdCard,
  faListCheck,
  faPills,
  faReceipt,
  faTrophy,
  faUsersRectangle,
  faShieldHalved,
  faVial,
  faWandMagicSparkles,
  faWineBottle,
  type IconDefinition
} from '@fortawesome/free-solid-svg-icons';

import { UserPermissions } from '@/types/user';
import type { MatchableNavItem } from '@/utils/resolveCurrentNavLabel';
import {
  PEOPLE_VIEW_AI_PROFILE,
  hasPermission,
  isInGroup,
  BILLING_SUPER_ADMIN_GROUP,
  ENGINEERING_GROUP,
  PEOPLE_VIEW_REWARD_CARDS,
  PEOPLE_IMPERSONATE,
  USER_RESET_MFA,
  PEOPLE_AVAILABILITY_VIEW,
  PEOPLE_VIEW_TIMELINE
} from '@/utils/userPermission';

export interface PersonNavDefinition extends MatchableNavItem {
  icon: IconDefinition;
}

export const getPersonNavOptions = (
  personId: string,
  permissions: UserPermissions
): PersonNavDefinition[] => {
  const root = `/people/${personId}`;

  return [
    {
      label: 'Profile',
      path: `${root}/profile`,
      icon: faIdCard,
      // The bare person route renders the profile, so Profile owns it too.
      alsoMatches: [root]
    },
    { label: 'Appointments', path: `${root}/appointments`, icon: faCalendarCheck },
    { label: 'Drug Screens', path: `${root}/drug-screens`, icon: faVial },
    { label: 'Treatability', path: `${root}/treatability`, icon: faClipboardCheck },
    { label: 'Tasks', path: `${root}/tasks`, icon: faListCheck },
    ...(hasPermission({ permissions, requiredPermission: PEOPLE_VIEW_TIMELINE })
      ? [{ label: 'Timeline', path: `${root}/timeline`, icon: faClockRotateLeft }]
      : []),
    { label: 'Community', path: `${root}/community`, icon: faUsersRectangle },
    { label: 'Medications', path: `${root}/medications`, icon: faPills },
    { label: 'Metrics', path: `${root}/metrics`, icon: faChartLine },
    { label: 'Messaging', path: `${root}/messaging`, icon: faComments },
    ...(hasPermission({ permissions, requiredPermission: PEOPLE_AVAILABILITY_VIEW })
      ? [{ label: 'Availability', path: `${root}/availability`, icon: faCalendarPlus }]
      : []),
    ...(hasPermission({ permissions, requiredPermission: PEOPLE_VIEW_AI_PROFILE })
      ? [{ label: 'AI Profile', path: `${root}/ai-profile`, icon: faWandMagicSparkles }]
      : []),
    ...(hasPermission({ permissions, requiredPermission: PEOPLE_VIEW_REWARD_CARDS })
      ? [
          { label: 'Reward Cards', path: `${root}/reward-cards`, icon: faCreditCard },
          { label: 'Transactions', path: `${root}/transactions`, icon: faReceipt }
        ]
      : []),
    ...((isInGroup(permissions, ENGINEERING_GROUP) &&
      hasPermission({ permissions, requiredPermission: PEOPLE_IMPERSONATE })) ||
    hasPermission({ permissions, requiredPermission: USER_RESET_MFA })
      ? [{ label: 'Authentication', path: `${root}/authentication`, icon: faShieldHalved }]
      : []),
    { label: 'Substance Tracker', path: `${root}/substance-tracker`, icon: faWineBottle },
    { label: 'Scheduling', path: `${root}/scheduling`, icon: faCalendarPlus },
    { label: 'Insurance', path: `${root}/insurance`, icon: faFileLines },
    ...(isInGroup(permissions, BILLING_SUPER_ADMIN_GROUP)
      ? [
          {
            label: 'Billing & VOB Timeline',
            path: `${root}/billing/timeline`,
            icon: faFileInvoiceDollar
          }
        ]
      : []),
    { label: 'Documents', path: `${root}/documents`, icon: faFileLines },
    { label: 'Achievements', path: `${root}/achievements`, icon: faTrophy },
    { label: 'Challenges', path: `${root}/challenges`, icon: faFlagCheckered },
    {
      label: 'Program Qualifications',
      path: `${root}/program-qualifications`,
      icon: faClipboardList
    }
  ];
};
